import { DisplayFeatureStatic } from '~shared/types/pricing/features';
import { SummaryFeatureRuleMap, DisplayFeature } from '../types';
import { formatSummaryFeature } from '~app/pages/Pricing/lib/packageFeatures';
import { Packages } from '~shared/constants/pricing';
import { GUAC_COUNTRY_CODES } from '~app/pages/Pricing/lib/constants';

const SETTING_UNLIMITED = 'unlimited';

const singleEnabledFeaturesRules = [
  'quizzes_pro',
  'analyze_export_enabled',
  'analyze_export_spss_enabled',
  'create_custom_variables_enabled',
  'file_upload',
  'click_map_qt',
  'analyze_can_export_hide_branding',
  'analyze_combine_filters',
  'analyze_trends_enabled',
  'collector_completion_url_enabled',
  'collector_popup_enabled',
  'collector_white_label_enabled',
  'multiple_block_rotation_enabled',
  'response_quality_enabled',
  'single_sign_on',
  'analyze_can_share_customize_branding',
].reduce(
  (acc, feature): SummaryFeatureRuleMap => ({
    ...acc,
    [feature]: ({ [feature]: featureItem }) =>
      featureItem?.isEnabled ? formatSummaryFeature(feature as keyof DisplayFeatureStatic) : null,
  }),
  {}
);

const nonConditionalFeatureRules = [
  'analyze_integrations',
  'hipaa_enabled',
  'private_apps',
  'gather_comments',
  'team_logic',
  'advanced_logic',
  'admin_dashboard',
  'analyze_results_together',
  'response_alerts',
  'advanced_analyze_features',
  'block_randomization',
  'flexible_plan_types',
  'integrations',
  'account_control',
  'enhanced_governance',
  'team_activity',
  'customer_success',
  'unlimited_questions',
  'extended_piping',
].reduce(
  (acc, feature): SummaryFeatureRuleMap => ({
    ...acc,
    [feature]: () => formatSummaryFeature(feature as keyof DisplayFeatureStatic),
  }),
  {}
);

// Individual feature line rules
const FeatureRules: SummaryFeatureRuleMap = {
  ...nonConditionalFeatureRules,
  ...singleEnabledFeaturesRules,

  create_limit: ({ survey_create_limit, create_question_limit }) => {
    let obj: DisplayFeatureStatic = {};

    if (survey_create_limit?.setting === SETTING_UNLIMITED) {
      obj = formatSummaryFeature('survey_create_limit');
    } else if (create_question_limit?.setting === SETTING_UNLIMITED) {
      obj = formatSummaryFeature('create_question_limit');
    }

    return obj;
  },

  response_count: ({ base_response_count, num_free_responses }, { locale }) => {
    let obj: DisplayFeatureStatic = {};

    // Annual packages
    if (base_response_count?.amount) {
      obj = formatSummaryFeature('base_response_count', { amount: base_response_count.amount.toLocaleString(locale) });

      // Basic/Standard monthly
    } else if (num_free_responses?.amount) {
      obj = formatSummaryFeature('num_free_responses', { amount: num_free_responses.amount.toLocaleString(locale) });

      // Enterprise
    } else if (num_free_responses?.setting === SETTING_UNLIMITED) {
      obj = formatSummaryFeature('unlimited_responses');
    }

    return obj;
  },

  support: ({ priority_email_support, phone_support }) => {
    let obj: DisplayFeatureStatic = {};

    // we'll check from highest level support to lowest
    if (phone_support?.isEnabled) {
      obj = formatSummaryFeature('phone_support');
    } else if (priority_email_support?.isEnabled) {
      obj = formatSummaryFeature('priority_email_support');
    } else {
      obj = formatSummaryFeature('email_support');
    }

    return obj;
  },

  skip_logic: ({ create_skip_logic_enabled, create_piping_enabled, funneling_enabled }) => {
    let obj: DisplayFeature = {};

    if (create_piping_enabled?.isEnabled || funneling_enabled?.isEnabled) {
      obj = formatSummaryFeature('create_piping_enabled');
    } else if (create_skip_logic_enabled?.isEnabled) {
      obj = formatSummaryFeature('skip_logic');
    }

    return obj;
  },

  survey_branding: ({ create_logo_enabled, create_custom_theme_enabled, collector_friendly_url_enabled }) => {
    const hasBranding =
      create_logo_enabled?.isEnabled &&
      create_custom_theme_enabled?.isEnabled &&
      collector_friendly_url_enabled?.isEnabled;
    return hasBranding ? formatSummaryFeature('survey_branding') : null;
  },

  collaborate: ({ collaboration, build_surveys_together, share_with_your_organization }) => {
    const hasCollaboration =
      collaboration?.isEnabled || build_surveys_together?.isEnabled || share_with_your_organization?.isEnabled;
    return hasCollaboration ? formatSummaryFeature('collaborate') : null;
  },

  create_advanced_features: ({ create_quotas_enabled, a_b_testing, create_randomization_enabled }) => {
    const hasAdvancedFeatures =
      create_quotas_enabled?.isEnabled || a_b_testing?.isEnabled || create_randomization_enabled?.isEnabled;
    return hasAdvancedFeatures ? formatSummaryFeature('create_advanced_features') : null;
  },

  analyze_response_limit: ({ analyze_response_limit }) =>
    analyze_response_limit?.setting === SETTING_UNLIMITED ? formatSummaryFeature('analyze_response_limit') : null,

  footer_branding: ({
    create_toggle_footer_enabled,
    disable_footer_branding_enabled,
    hide_export_branding_enabled,
  }) => {
    const hasFooterLogic =
      create_toggle_footer_enabled?.isEnabled ||
      disable_footer_branding_enabled?.isEnabled ||
      hide_export_branding_enabled?.isEnabled;
    return hasFooterLogic ? formatSummaryFeature('footer_branding') : null;
  },

  shared_assets: ({
    asset_library,
    group_templates,
    lite_library,
    shared_library,
    shared_group_library,
    team_themes,
    custom_templates,
  }) => {
    const hasSharedAssets =
      asset_library?.isEnabled ||
      group_templates?.isEnabled ||
      lite_library?.isEnabled ||
      shared_library?.isEnabled ||
      shared_group_library?.isEnabled ||
      team_themes?.isEnabled ||
      custom_templates?.isEnabled;
    return hasSharedAssets ? formatSummaryFeature('shared_assets') : null;
  },

  benchmark_logic: ({ plans_benchmarks, analyze_access_benchmarking, benchmarks }) => {
    const hasBenchmarkLogic =
      plans_benchmarks?.isEnabled || analyze_access_benchmarking?.isEnabled || benchmarks?.isEnabled;
    return hasBenchmarkLogic ? formatSummaryFeature('benchmark_logic') : null;
  },

  sentiment: ({ analyze_sentiment_enabled, sa_en_ga }, { packageName, countryCode }) => {
    const hasSentiment = analyze_sentiment_enabled?.isEnabled && sa_en_ga?.isEnabled;
    const allowedPackages = [
      Packages.PremierMonthly,
      Packages.PremierAnnual,
      Packages.TeamPremier,
      Packages.Enterprise,
    ];
    return hasSentiment &&
      packageName &&
      allowedPackages.includes(packageName) &&
      countryCode &&
      GUAC_COUNTRY_CODES.includes(countryCode)
      ? formatSummaryFeature('sentiment')
      : null;
  },

  crosstabs: ({ show_create_crosstab, filter_crosstab }) => {
    const hasCrosstabs = show_create_crosstab?.isEnabled || filter_crosstab?.isEnabled;
    return hasCrosstabs ? formatSummaryFeature('show_create_crosstab') : null;
  },

  statistical_significance: ({ statistical_significance, show_sig_diffs_enabled }) => {
    const hasSignificance = statistical_significance?.isEnabled || show_sig_diffs_enabled?.isEnabled;
    return hasSignificance ? formatSummaryFeature('statistical_significance') : null;
  },

  all_data_exports: ({ analyze_export_enabled, analyze_export_spss_enabled }) => {
    const hasAllDataExports = analyze_export_enabled?.isEnabled && analyze_export_spss_enabled?.isEnabled;
    return hasAllDataExports ? formatSummaryFeature('all_data_exports') : null;
  },

  contributor_seats: (_, { packageName }) => {
    return formatSummaryFeature(packageName === Packages.Enterprise ? 'advanced_collaboration' : 'contributor_seats');
  },

  multilingual: (_, { packageName, countryCode }) => {
    const disAllowedPackages = [Packages.AdvantageMonthly, Packages.AdvantageAnnual, Packages.TeamAdvantage];

    return (packageName && !disAllowedPackages.includes(packageName)) ||
      (countryCode && !GUAC_COUNTRY_CODES.includes(countryCode))
      ? formatSummaryFeature('multilingual')
      : null;
  },

  payment_qt: ({ payment_qt }, { dataCenter }) => {
    return payment_qt?.isEnabled && dataCenter !== 'EU' ? formatSummaryFeature('payment_qt') : null;
  },
};

export default FeatureRules;
